import { useAnalytics } from '@/contexts/AnalyticsContext';
import { useDashboardContext } from '@/contexts/DashboardContext';
import { UserContext } from '@/contexts/UserContext';
import { getCachedCampaigns } from '@/services/AdsAccountsService';
import { PromptAttachment } from '@/types/agentChat';
import { classNames } from '@/utils';
import { getAdsPlatformIcon } from '@/utils/assets';
import {
  AdsAccount,
  CampaignAgentRunAttachment,
  CampaignProvider,
} from '@superfunnel/types';
import Button from '@superfunnel/ui-core/v2/Button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@superfunnel/ui-core/v2/Command';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@superfunnel/ui-core/v2/DropdownMenu';
import { Separator } from '@superfunnel/ui-core/v2/Separator';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@superfunnel/ui-core/v2/Tooltip';
import { useQuery } from '@tanstack/react-query';
import { QueryDocumentSnapshot } from 'firebase/firestore';
import { Check, Link, Settings, ThumbsUp } from 'iconoir-react';
import { Dispatch, SetStateAction, useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';

/** Campaign data from the ad platform for selection in the picker */
export type CampaignPickerItem = Pick<
  CampaignAgentRunAttachment,
  'name' | 'campaignId' | 'provider'
>;

interface ICampaign {
  size?: 'sm' | 'lg';
  promptContexts: PromptAttachment[];
  setPromptContexts: Dispatch<SetStateAction<PromptAttachment[]>>;
  asSubMenu?: boolean;
}

const Skeleton = ({ className }: { className?: string }) => (
  <div className={`bg-base-200 animate-pulse rounded ${className}`} />
);

const CampaignsSubMenu = ({
  providerKey,
  apiProvider,
  label,
  icon,
  workspaceId,
  accounts,
  onSelect,
  currentCampaigns,
}: {
  providerKey: Extract<CampaignProvider, 'google' | 'facebook' | 'taboola'>;
  apiProvider: string;
  label: string;
  icon: string;
  workspaceId: string;
  accounts: QueryDocumentSnapshot<AdsAccount, AdsAccount>[];
  onSelect: (campaign: CampaignPickerItem) => void;
  currentCampaigns: CampaignPickerItem[];
}) => {
  const { selectedAccount } = useContext(UserContext);
  const navigate = useNavigate();

  const { data: campaigns = [], isLoading: loading } = useQuery({
    queryKey: [
      'campaigns',
      workspaceId,
      apiProvider,
      accounts.map((a) => a.id),
    ],
    queryFn: async () => {
      // Fetch campaigns for all connected accounts of this provider
      const filters = accounts.map((acc) => ({
        adsAccountId: acc.id,
        provider: acc.data().provider,
      }));

      return getCachedCampaigns(workspaceId, [apiProvider], filters);
    },
    enabled: accounts.length > 0,
  });

  return (
    <DropdownMenuSub>
      <DropdownMenuSubTrigger className={'gap-2 py-1 pr-2 pl-0.5'}>
        <img src={icon} alt={label} className={'size-6'} />
        {label}
      </DropdownMenuSubTrigger>
      <DropdownMenuSubContent
        className={'w-80'}
        alignOffset={12}
        sideOffset={12}
        collisionPadding={16}
      >
        <Command onClick={(e) => e.stopPropagation()}>
          <CommandInput
            placeholder="Search campaigns..."
            autoFocus={true}
            className="h-9 border-transparent px-1 focus-visible:ring-0 focus-visible:ring-offset-0"
          />
          <CommandList>
            {loading ? (
              <div className="space-y-2 p-2">
                <Skeleton className="h-6 w-full" />
                <Skeleton className="h-6 w-full" />
                <Skeleton className="h-6 w-full" />
                <Skeleton className="h-6 w-full" />
                <Skeleton className="h-6 w-full" />
                <Skeleton className="h-6 w-full" />
                <Skeleton className="h-6 w-full" />
              </div>
            ) : (
              <>
                <CommandEmpty>No campaigns found.</CommandEmpty>
                <CommandGroup>
                  {campaigns.map((item) => {
                    const isDisabled = currentCampaigns.length > 0;

                    return (
                      <CommandItem
                        key={item.id}
                        value={`${item.id} ${item.name}`}
                        onSelect={() => {
                          onSelect({
                            name: item.name,
                            campaignId: Number(item.id),
                            provider: providerKey,
                          });
                        }}
                        disabled={isDisabled}
                        className={
                          'flex flex-col items-start justify-between gap-0'
                        }
                      >
                        <span className="w-full truncate">{item.name}</span>
                        <span className={'text-base-400 text-xs'}>
                          {item.id}
                        </span>
                      </CommandItem>
                    );
                  })}
                </CommandGroup>
              </>
            )}
          </CommandList>
        </Command>
        <Separator orientation="horizontal" className={'mb-1'} />
        <DropdownMenuItem
          className={'p-2'}
          onSelect={() => {
            navigate(
              `/workspace/${selectedAccount?.data().slug}/settings/ad-accounts`,
            );
          }}
        >
          <Settings className={'size-5'} />
          Manage ad account
        </DropdownMenuItem>
      </DropdownMenuSubContent>
    </DropdownMenuSub>
  );
};
export const ChatInputSelectCampaign = ({
  size = 'sm',
  promptContexts,
  setPromptContexts,
  asSubMenu = false,
}: ICampaign) => {
  const { track } = useAnalytics();
  const { adsAccounts } = useDashboardContext();
  const { selectedAccount } = useContext(UserContext);
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [integrationsVoted, setIntegrationsVoted] = useState<string[]>([]);

  const providers: {
    key: CampaignProvider;
    label: string;
    icon: string;
    provider: string;
  }[] = [
    {
      key: 'google',
      label: 'Google Ads',
      icon: getAdsPlatformIcon('google'),
      provider: 'google',
    },
    {
      key: 'facebook',
      label: 'Meta Ads',
      icon: getAdsPlatformIcon('facebook'),
      provider: 'facebook',
    },
    {
      key: 'taboola',
      label: 'Taboola',
      icon: getAdsPlatformIcon('taboola'),
      provider: 'taboola',
    },
    {
      key: 'linkedin',
      label: 'LinkedIn Ads',
      icon: getAdsPlatformIcon('linkedin'),
      provider: 'linkedin',
    },
    {
      key: 'tiktok',
      label: 'TikTok Ads',
      icon: getAdsPlatformIcon('tiktok'),
      provider: 'tiktok',
    },
    {
      key: 'bing',
      label: 'Bing Ads',
      icon: getAdsPlatformIcon('bing'),
      provider: 'bingads',
    },
  ] as const;

  // Helper to get current campaigns from promptContexts
  const currentCampaigns: CampaignPickerItem[] = promptContexts
    .filter(
      (ctx): ctx is Extract<PromptAttachment, { type: 'campaign' }> =>
        ctx.type === 'campaign',
    )
    .map((ctx) => ({
      name: ctx.name,
      campaignId: ctx.campaignId,
      provider: ctx.provider,
    }));

  // Helper to add a campaign
  const addCampaign = (campaign: CampaignPickerItem) => {
    const newCampaignAttachment: PromptAttachment = {
      id: `campaign-${campaign.campaignId}-${Date.now()}`,
      type: 'campaign',
      name: campaign.name,
      campaignId: campaign.campaignId,
      provider: campaign.provider,
    };
    setPromptContexts((prev) => [...prev, newCampaignAttachment]);
    if (!asSubMenu) {
      setOpen(false);
    }
  };

  const menuContent = providers.map((p) => {
    // Filter connected accounts for this provider
    const connectedAccounts =
      adsAccounts?.filter((acc) => acc.data().provider === p.provider) || [];
    const isConnected = connectedAccounts.length > 0;

    if (isConnected && selectedAccount) {
      return (
        <CampaignsSubMenu
          key={p.key}
          providerKey={
            p.key as Extract<
              CampaignProvider,
              'google' | 'facebook' | 'taboola'
            >
          }
          apiProvider={p.provider}
          label={p.label}
          icon={p.icon}
          workspaceId={selectedAccount.id}
          accounts={connectedAccounts}
          onSelect={addCampaign}
          currentCampaigns={currentCampaigns}
        />
      );
    }

    const comingSoon = ['taboola', 'linkedin', 'tiktok', 'bing'].includes(
      p.key,
    );

    return (
      <DropdownMenuItem
        key={p.key}
        disabled={comingSoon}
        className={classNames(
          'py-1 pr-2 pl-0.5 data-disabled:bg-none! data-disabled:opacity-100',
          comingSoon ? 'hover:bg-transparent' : '',
        )}
        onSelect={() => {
          track('agent_connect_ad_account_click', { provider: p.provider });
          navigate(
            `/workspace/${selectedAccount?.data().slug}/settings/ad-accounts`,
          );
        }}
      >
        <img src={p.icon} alt={p.label} className={'size-6'} />
        <span className="mr-1">Connect {p.label}</span>
        {comingSoon && (
          <>
            <span className="ml-auto text-xs text-gray-400">Coming Soon</span>
            <span
              className="pointer-events-auto -me-1"
              onClick={(e) => e.stopPropagation()}
              onMouseEnter={(e) => e.stopPropagation()}
            >
              <Tooltip>
                <TooltipTrigger>
                  <Button
                    size="sm"
                    variant={'ghost'}
                    disabled={integrationsVoted.includes(p.provider)}
                    onClick={() => {
                      track('agent_integration_vote_click', {
                        provider: p.provider,
                      });
                      setIntegrationsVoted((prev) => [...prev, p.provider]);
                    }}
                  >
                    {integrationsVoted.includes(p.provider) ? (
                      <Check className="text-success-500" />
                    ) : (
                      <ThumbsUp />
                    )}
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="right">
                  {integrationsVoted.includes(p.provider)
                    ? 'Thanks for voting!'
                    : 'Vote to get this integration faster'}
                </TooltipContent>
              </Tooltip>
            </span>
          </>
        )}
      </DropdownMenuItem>
    );
  });

  if (asSubMenu) {
    return (
      <DropdownMenuSub>
        <DropdownMenuSubTrigger className={'gap-2 p-2'}>
          <Link className={'size-5'} />
          Select campaign
        </DropdownMenuSubTrigger>
        <DropdownMenuSubContent
          sideOffset={12}
          className={'[&_svg]:text-base-500 w-max space-y-0.5 p-2'}
        >
          {menuContent}
        </DropdownMenuSubContent>
      </DropdownMenuSub>
    );
  }

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          type="button"
          className={size === 'sm' ? 'h-7' : 'h-9'}
        >
          <Link className={'size-6 -rotate-45'} />
          Select campaign
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align={'start'}
        className={'[&_svg]:text-base-500 w-max space-y-0.5 p-2'}
      >
        {menuContent}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
